import { reportApi } from "./api"
import type {
  components,
  operations,
} from "./generated/mm-service-portal.schema"

export type ServiceAccount = components["schemas"]["ServiceAccountMetadata"]
export type ServiceAccountApiKey =
  components["schemas"]["ServiceAccountApiKeyRecord"]
export type ServiceAccountApiKeyCreateRequest =
  operations["createServiceAccountApiKey"]["requestBody"]["content"]["application/json"]
export type ServiceAccountApiKeyCreateResponse =
  operations["createServiceAccountApiKey"]["responses"]["201"]["content"]["application/json"]
export type ServicePortalError = components["schemas"]["Error"]

export type GetServiceAccountsParams =
  operations["listServiceAccounts"]["parameters"]["query"]
export type GetServiceAccountsResponse =
  operations["listServiceAccounts"]["responses"]["200"]["content"]["application/json"]
export type CreateServiceAccountParams =
  operations["createServiceAccount"]["parameters"]["path"]
export type CreateServiceAccountResponse =
  operations["createServiceAccount"]["responses"]["201"]["content"]["application/json"]
export type GetServiceAccountApiKeysParams =
  operations["listServiceAccountApiKeys"]["parameters"]["path"]
export type GetServiceAccountApiKeysResponse =
  operations["listServiceAccountApiKeys"]["responses"]["200"]["content"]["application/json"]
export type CreateServiceAccountApiKeyArgs =
  operations["createServiceAccountApiKey"]["parameters"]["path"] &
    Partial<ServiceAccountApiKeyCreateRequest> &
    Partial<GetServiceAccountsParams>
export type DeleteServiceAccountApiKeyArgs =
  operations["deleteServiceAccountApiKey"]["parameters"]["path"] &
    Partial<GetServiceAccountsParams>

export const serviceAccountsApi = reportApi.injectEndpoints({
  endpoints: (builder) => ({
    getServiceAccounts: builder.query<
      GetServiceAccountsResponse,
      GetServiceAccountsParams
    >({
      query: (params) => ({
        url: "/service-accounts",
        params,
      }),
      providesTags: (result, _error, { org_id }) => [
        { type: "OrgServiceAccounts" as const, id: "LIST" },
        { type: "OrgServiceAccounts" as const, id: org_id },
        ...(result?.data.map((serviceAccount) => ({
          type: "ServiceAccountApiKeys" as const,
          id: serviceAccount.service_account_id,
        })) ?? []),
      ],
    }),
    createServiceAccount: builder.mutation<
      CreateServiceAccountResponse,
      CreateServiceAccountParams
    >({
      query: ({ org_id }) => ({
        url: `/service-accounts/${org_id}`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: (_result, _error, { org_id }) => [
        { type: "OrgServiceAccounts" as const, id: "LIST" },
        { type: "OrgServiceAccounts" as const, id: org_id },
      ],
    }),
    getServiceAccountApiKeys: builder.query<
      GetServiceAccountApiKeysResponse,
      GetServiceAccountApiKeysParams
    >({
      query: ({ service_account }) => ({
        url: `/service-account/${service_account}/api-keys`,
      }),
      providesTags: (_result, _error, { service_account }) => [
        { type: "ServiceAccountApiKeys" as const, id: service_account },
      ],
    }),
    createServiceAccountApiKey: builder.mutation<
      ServiceAccountApiKeyCreateResponse,
      CreateServiceAccountApiKeyArgs
    >({
      query: ({ service_account, expires_at }) => ({
        url: `/service-account/${service_account}/api-keys`,
        method: "POST",
        body: {
          expires_at,
        },
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: (_result, _error, { service_account, org_id }) => [
        { type: "ServiceAccountApiKeys" as const, id: service_account },
        { type: "OrgServiceAccounts" as const, id: "LIST" },
        ...(org_id
          ? [{ type: "OrgServiceAccounts" as const, id: org_id }]
          : []),
      ],
    }),
    deleteServiceAccountApiKey: builder.mutation<
      void,
      DeleteServiceAccountApiKeyArgs
    >({
      query: ({ service_account, key_id }) => ({
        url: `/service-account/${service_account}/api-keys/${key_id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { service_account, org_id }) => [
        { type: "ServiceAccountApiKeys" as const, id: service_account },
        { type: "OrgServiceAccounts" as const, id: "LIST" },
        ...(org_id
          ? [{ type: "OrgServiceAccounts" as const, id: org_id }]
          : []),
      ],
    }),
  }),
})

export const {
  useGetServiceAccountsQuery,
  useCreateServiceAccountMutation,
  useGetServiceAccountApiKeysQuery,
  useCreateServiceAccountApiKeyMutation,
  useDeleteServiceAccountApiKeyMutation,
} = serviceAccountsApi
